import {
  type InfiniteScrollCustomEvent,
  IonContent,
  IonInfiniteScroll,
  IonInfiniteScrollContent,
  IonLabel,
  IonPage,
  IonRefresher,
  IonRefresherContent,
  IonSegment,
  IonSegmentButton,
  IonSkeletonText,
  type RefresherEventDetail,
} from '@ionic/react';
import type { UseInfiniteQueryResult } from '@tanstack/react-query';
import { useState } from 'react';
import { Redirect } from 'react-router-dom';

import type { Company } from '~/api/auth';
import {
  type CompanyDocument,
  GUIDE_STATUS,
  GUIDE_STATUS_FILTERS,
  type GuideStatusFilter,
  type TaxGuide,
  isGuideStatusFilter,
} from '~/api/documents';
import { useCompany } from '~/api/hooks/useCompany';
import { type GuidePages, useGuidesInfiniteQuery } from '~/api/hooks/useDocuments';
import { CompanySwitcher } from '~/components/CompanySwitcher';
import { useTenant } from '~/components/TenantGate';
import { Icon } from '~/components/ui/Icon';
import { useAppNavigation } from '~/hooks/useAppNavigation';
import { DocumentoSheet } from '~/pages/documentos/DocumentoSheet';
import { officeShortName } from '~/pages/documentos/PostCard';
import { GuideCard } from '~/pages/impostos/GuideCard';
import { ROUTES } from '~/routes';

type GuidesQuery = UseInfiniteQueryResult<GuidePages, Error>;
type GuideOrder = (first: TaxGuide, second: TaxGuide) => number;

const DEFAULT_SEGMENT = GUIDE_STATUS.open;
const SKELETON_CARDS = [0, 1, 2];

const SEGMENT_LABEL: Record<GuideStatusFilter, string> = {
  open: 'Em aberto',
  overdue: 'Vencidas',
  paid: 'Pagas',
};

const EMPTY_TITLE: Record<GuideStatusFilter, string> = {
  open: 'Nenhuma guia em aberto',
  overdue: 'Nenhuma guia vencida',
  paid: 'Nenhuma guia paga',
};

const EMPTY_DETAIL: Record<GuideStatusFilter, (officeName: string) => string> = {
  open: (officeName) => `Quando ${officeName} publicar uma guia desta empresa, ela aparece aqui.`,
  overdue: () => 'Nenhuma guia em aberto passou do vencimento.',
  paid: () => 'As guias marcadas como pagas aparecem aqui.',
};

function byDueDate(first: TaxGuide, second: TaxGuide): number {
  return first.due_date.localeCompare(second.due_date);
}

/** A paga mais recente primeiro; uma paga sem data (não deveria existir) por último. */
function byLatestPayment(first: TaxGuide, second: TaxGuide): number {
  if (first.paid_at === second.paid_at) return 0;
  if (first.paid_at === null) return 1;
  if (second.paid_at === null) return -1;
  return Date.parse(second.paid_at) - Date.parse(first.paid_at);
}

/** A lista vem por cursor na ordem de publicação; a tela ordena o que já chegou. */
const GUIDE_ORDER: Record<GuideStatusFilter, GuideOrder> = {
  open: byDueDate,
  overdue: byDueDate,
  paid: byLatestPayment,
};

/**
 * As guias em aberto e as vencidas pelo vencimento, a mais próxima primeiro; as pagas pela data do
 * pagamento, a mais recente primeiro. Um documento sem guia vai para o fim.
 */
export function sortGuides(
  documents: readonly CompanyDocument[],
  status: GuideStatusFilter,
): CompanyDocument[] {
  const order = GUIDE_ORDER[status];
  return [...documents].sort((first, second) => {
    if (!first.guide || !second.guide) return Number(!first.guide) - Number(!second.guide);
    return order(first.guide, second.guide);
  });
}

function hasGuide(companyDocument: CompanyDocument): boolean {
  return companyDocument.guide !== null;
}

function documentsOf(guides: GuidesQuery): CompanyDocument[] {
  return guides.data?.pages.flatMap((page) => page.results) ?? [];
}

/** O segmento tocado; um valor que não é de segmento nenhum mantém o atual. */
function segmentStatus(value: unknown, current: GuideStatusFilter): GuideStatusFilter {
  return isGuideStatusFilter(value) ? value : current;
}

async function loadMoreGuides(
  guides: GuidesQuery,
  event: InfiniteScrollCustomEvent,
): Promise<void> {
  if (guides.hasNextPage && !guides.isFetchingNextPage) {
    await guides.fetchNextPage();
  }
  await event.target.complete();
}

async function refreshGuides(
  guides: GuidesQuery,
  event: CustomEvent<RefresherEventDetail>,
): Promise<void> {
  await guides.refetch();
  event.detail.complete();
}

/**
 * `/impostos`: as guias da empresa em operação em três segmentos (em aberto, vencidas e pagas),
 * cada um com o valor, o vencimento, "Copiar código Pix" e, com `documents` `FULL`, o "Paguei".
 * Fica atrás de `RequireModule` (`documents` `READ`), como o feed; sem empresa, volta ao painel.
 * O toque num lembrete abre `?empresa=<id>`, e o `CompanyFromUrl` do `App.tsx` põe essa empresa
 * em operação antes do guarda.
 */
export function Impostos(): JSX.Element {
  const { selected } = useCompany();

  return (
    <IonPage>
      <IonContent fullscreen>
        {selected ? <CompanyGuides company={selected} /> : <Redirect to={ROUTES.dashboard} />}
      </IonContent>
    </IonPage>
  );
}

function CompanyGuides({ company }: { company: Company }): JSX.Element {
  const nav = useAppNavigation();
  const tenant = useTenant();
  const [status, setStatus] = useState<GuideStatusFilter>(DEFAULT_SEGMENT);
  const [openedDocument, setOpenedDocument] = useState<CompanyDocument | null>(null);
  const guides = useGuidesInfiniteQuery(company.id, status);
  const documents = sortGuides(documentsOf(guides), status);

  return (
    <>
      <IonRefresher slot="fixed" onIonRefresh={(event) => refreshGuides(guides, event)}>
        <IonRefresherContent />
      </IonRefresher>

      <div className="rx-container rx-screen pb-7">
        <div className="px-6 pt-3">
          <button
            type="button"
            className="rx-iconbtn"
            onClick={() => nav.replace(ROUTES.dashboard)}
            aria-label="Voltar"
          >
            <Icon name="back" size={18} />
          </button>
          <h1 className="mb-0 mt-3.5 text-[30px] font-medium tracking-display-tight text-ink">
            Impostos
          </h1>
          <div className="mt-2">
            <CompanySwitcher compact />
          </div>
          <IonSegment
            className="mt-4"
            value={status}
            onIonChange={(event) => setStatus(segmentStatus(event.detail.value, status))}
          >
            {GUIDE_STATUS_FILTERS.map((segment) => (
              <IonSegmentButton key={segment} value={segment}>
                <IonLabel>{SEGMENT_LABEL[segment]}</IonLabel>
              </IonSegmentButton>
            ))}
          </IonSegment>
        </div>

        <div className="flex flex-col gap-3 px-6 pt-5">
          {guides.isPending ? (
            <GuidesSkeleton />
          ) : documents.some(hasGuide) ? (
            documents.map((companyDocument) => (
              <GuideCard
                key={companyDocument.id}
                companyDocument={companyDocument}
                clientId={company.id}
                onOpen={setOpenedDocument}
              />
            ))
          ) : (
            <EmptyGuides status={status} officeName={officeShortName(tenant)} />
          )}
        </div>

        <IonInfiniteScroll
          onIonInfinite={(event) => loadMoreGuides(guides, event)}
          disabled={!guides.hasNextPage}
          threshold="200px"
        >
          <IonInfiniteScrollContent loadingSpinner="dots" loadingText="Carregando mais guias..." />
        </IonInfiniteScroll>
      </div>

      <DocumentoSheet
        companyDocument={openedDocument}
        clientId={company.id}
        onClose={() => setOpenedDocument(null)}
      />
    </>
  );
}

function GuidesSkeleton(): JSX.Element {
  return (
    <output aria-label="Carregando as guias" className="flex flex-col gap-3">
      {SKELETON_CARDS.map((index) => (
        <div key={index} className="rx-card">
          <IonSkeletonText animated style={{ width: '45%', height: 15 }} />
          <IonSkeletonText animated style={{ width: '35%', height: 24, marginTop: 10 }} />
          <IonSkeletonText animated style={{ width: '60%', height: 13, marginTop: 8 }} />
        </div>
      ))}
    </output>
  );
}

interface EmptyGuidesProps {
  status: GuideStatusFilter;
  officeName: string;
}

function EmptyGuides({ status, officeName }: EmptyGuidesProps): JSX.Element {
  return (
    <div className="rx-card text-center" style={{ padding: '28px 16px' }}>
      <span
        aria-hidden="true"
        className="inline-flex h-[52px] w-[52px] items-center justify-center rounded-full bg-brand-tint text-brand"
      >
        <Icon name="ledger" size={22} />
      </span>
      <p className="mb-0 mt-3.5 text-overline font-semibold uppercase text-ink-subtle">
        {EMPTY_TITLE[status]}
      </p>
      <p className="mx-auto mb-0 mt-2 max-w-[32ch] text-[13px] text-ink-muted">
        {EMPTY_DETAIL[status](officeName)}
      </p>
    </div>
  );
}
